import type { BatchEncodeItem, EncodingSystem } from "./types";
import {
  DEFAULT_ENCODING_SYSTEM,
  getEncodingProfile,
  isNonEnglishEncodingSystem,
  matchesProfileLanguage,
  matchesProfileWikipediaUrl,
} from "./encodingProfiles";
import type { NonEnglishEncodingSystem } from "./encodingProfiles";

export type ShareEncodingMode = "optimal" | "random";

export type SharedMapping = {
  encodingSystem: EncodingSystem;
  items: BatchEncodeItem[];
  mode: ShareEncodingMode;
};

export type SharedMappingReadResult =
  | { kind: "none" }
  | { kind: "valid"; mapping: SharedMapping }
  | { kind: "invalid"; message: string };

export type ClipboardWriter = {
  writeText: (value: string) => Promise<void>;
};

export type MappingShareStorage = Pick<Storage, "getItem" | "removeItem" | "setItem">;

export type PendingMappingShareReadResult =
  | { kind: "none" }
  | { hash: string; kind: "valid"; mapping: SharedMapping };

type CompactSharePayload = {
  m: "o" | "r";
  r: Array<[string, Array<[string, string]>]>;
  v: 1;
};

type CompactProfileCandidate = [
  number: string,
  noun: string,
  pronunciation: string | null,
  pronunciationDialect: string | null,
  pronunciationVariant: string | null,
  labelLanguage: string,
  definition: string | null,
  wikipediaUrl: string | null,
];

type CompactSharePayloadV2 = {
  m: "o" | "r";
  r: Array<[string, CompactProfileCandidate[]]>;
  s: NonEnglishEncodingSystem;
  v: 2;
};

const HASH_PREFIX = "#mapping=";
const MAX_HASH_PAYLOAD_LENGTH = 16_384;
const MAX_ROWS = 10;
const MAX_DIGITS = 32;
const MAX_CANDIDATES = 32;
const MAX_NOUN_LENGTH = 200;
const INVALID_SHARE_MESSAGE = "Invalid or unsupported share link";
const PENDING_MAPPING_SHARE_STORAGE_KEY = "nummem.pendingMappingShare";
const PUBLIC_MAPPING_SHARE_ORIGIN = "https://nummem.com";
export const PENDING_MAPPING_SHARE_TTL_MS = 10 * 60 * 1_000;
export const MAPPING_AUTH_COMPLETE_PARAMETER = "mapping_auth";

type PendingMappingSharePayload = {
  createdAt: number;
  hash: string;
  v: 1;
};

export function createMappingShareHash(
  items: BatchEncodeItem[],
  mode: ShareEncodingMode,
  encodingSystem: EncodingSystem = DEFAULT_ENCODING_SYSTEM,
): string {
  if (
    encodingSystem !== DEFAULT_ENCODING_SYSTEM &&
    (
      !isNonEnglishEncodingSystem(encodingSystem) ||
      getEncodingProfile(encodingSystem).sharePayloadVersion !== 2
    )
  ) {
    throw new Error("The selected encoding system cannot be shared");
  }
  const payload: CompactSharePayload | CompactSharePayloadV2 =
    encodingSystem === DEFAULT_ENCODING_SYSTEM
      ? {
          m: mode === "random" ? "r" : "o",
          r: compactRows(items, encodingSystem),
          v: 1,
        }
      : {
          m: mode === "random" ? "r" : "o",
          r: compactRows(items, encodingSystem),
          s: encodingSystem,
          v: 2,
        };
  const encoded = encodeBase64Url(JSON.stringify(payload));
  if (encoded.length > MAX_HASH_PAYLOAD_LENGTH) {
    throw new Error("Current mapping is too large to share in one link");
  }
  return `${HASH_PREFIX}${encoded}`;
}

export function createMappingShareUrl(
  currentUrl: string,
  items: BatchEncodeItem[],
  mode: ShareEncodingMode,
  encodingSystem: EncodingSystem = DEFAULT_ENCODING_SYSTEM,
): string {
  const sourceUrl = new URL(currentUrl);
  if (sourceUrl.protocol !== "https:" && sourceUrl.protocol !== "http:") {
    throw new Error("Only HTTP or HTTPS mapping links can be shared");
  }
  const url = new URL(PUBLIC_MAPPING_SHARE_ORIGIN);
  url.pathname = sourceUrl.pathname;
  url.search = sourceUrl.search;
  url.hash = createMappingShareHash(items, mode, encodingSystem).slice(1);
  return url.toString();
}

export function readMappingShareHash(hash: string): SharedMappingReadResult {
  if (!hash.startsWith(HASH_PREFIX)) {
    return { kind: "none" };
  }
  const encoded = hash.slice(HASH_PREFIX.length);
  if (
    encoded.length === 0 ||
    encoded.length > MAX_HASH_PAYLOAD_LENGTH ||
    !/^[A-Za-z0-9_-]+$/.test(encoded)
  ) {
    return { kind: "invalid", message: INVALID_SHARE_MESSAGE };
  }
  try {
    const payload = JSON.parse(decodeBase64Url(encoded)) as unknown;
    if (
      !isRecord(payload) ||
      ![1, 2].includes(payload.v as number) ||
      (payload.m !== "o" && payload.m !== "r")
    ) {
      throw new Error(INVALID_SHARE_MESSAGE);
    }
    let encodingSystem: EncodingSystem;
    let items: BatchEncodeItem[];
    if (payload.v === 1) {
      encodingSystem = "english";
      items = expandEnglishRows(payload.r, payload.m === "r");
    } else {
      if (
        !isNonEnglishEncodingSystem(payload.s) ||
        getEncodingProfile(payload.s).sharePayloadVersion !== 2
      ) {
        throw new Error(INVALID_SHARE_MESSAGE);
      }
      encodingSystem = payload.s;
      items = expandProfileRows(payload.r, payload.m === "r", encodingSystem);
    }
    return {
      kind: "valid",
      mapping: {
        encodingSystem,
        items,
        mode: payload.m === "r" ? "random" : "optimal",
      },
    };
  } catch {
    return { kind: "invalid", message: INVALID_SHARE_MESSAGE };
  }
}

export async function copyMappingShareUrl(
  url: string,
  clipboard: ClipboardWriter | undefined,
): Promise<void> {
  if (!clipboard) {
    throw new Error("Clipboard access is unavailable in this browser");
  }
  await clipboard.writeText(url);
}

export function storePendingMappingShare(
  hash: string,
  storage: MappingShareStorage | undefined,
  now = Date.now(),
): boolean {
  const shareState = readMappingShareHash(hash);
  if (shareState.kind !== "valid" || !storage) {
    clearPendingMappingShare(storage);
    return false;
  }
  const payload: PendingMappingSharePayload = { createdAt: now, hash, v: 1 };
  try {
    storage.removeItem(PENDING_MAPPING_SHARE_STORAGE_KEY);
    storage.setItem(PENDING_MAPPING_SHARE_STORAGE_KEY, JSON.stringify(payload));
    return true;
  } catch {
    clearPendingMappingShare(storage);
    return false;
  }
}

export function readPendingMappingShare(
  storage: MappingShareStorage | undefined,
  now = Date.now(),
): PendingMappingShareReadResult {
  if (!storage) {
    return { kind: "none" };
  }
  try {
    const stored = storage.getItem(PENDING_MAPPING_SHARE_STORAGE_KEY);
    if (stored === null) {
      return { kind: "none" };
    }
    const payload = JSON.parse(stored) as unknown;
    if (
      !isRecord(payload) ||
      payload.v !== 1 ||
      typeof payload.hash !== "string" ||
      typeof payload.createdAt !== "number" ||
      !Number.isFinite(payload.createdAt) ||
      now < payload.createdAt ||
      now - payload.createdAt >= PENDING_MAPPING_SHARE_TTL_MS
    ) {
      storage.removeItem(PENDING_MAPPING_SHARE_STORAGE_KEY);
      return { kind: "none" };
    }
    const shareState = readMappingShareHash(payload.hash);
    if (shareState.kind !== "valid") {
      storage.removeItem(PENDING_MAPPING_SHARE_STORAGE_KEY);
      return { kind: "none" };
    }
    return { hash: payload.hash, kind: "valid", mapping: shareState.mapping };
  } catch {
    clearPendingMappingShare(storage);
    return { kind: "none" };
  }
}

export function clearPendingMappingShare(
  storage: MappingShareStorage | undefined,
): void {
  try {
    storage?.removeItem(PENDING_MAPPING_SHARE_STORAGE_KEY);
  } catch {
    return;
  }
}

function compactRows(
  items: BatchEncodeItem[],
  encodingSystem: "english",
): CompactSharePayload["r"];
function compactRows(
  items: BatchEncodeItem[],
  encodingSystem: NonEnglishEncodingSystem,
): CompactSharePayloadV2["r"];
function compactRows(
  items: BatchEncodeItem[],
  encodingSystem: EncodingSystem,
): CompactSharePayload["r"] | CompactSharePayloadV2["r"] {
  if (items.length === 0 || items.length > MAX_ROWS || items.some((item) => !item.ok)) {
    throw new Error(`A successful mapping with 1 to ${MAX_ROWS} rows is required`);
  }
  if (items.some(
    (item) =>
      item.ok &&
      (item.result.encoding_system ?? "english") !== encodingSystem,
  )) {
    throw new Error("Every shared row must use the selected encoding system");
  }
  if (isNonEnglishEncodingSystem(encodingSystem)) {
    const profile = getEncodingProfile(encodingSystem);
    const rows: CompactSharePayloadV2["r"] = items.map((item) => {
      if (!item.ok) {
        throw new Error("A successful mapping is required");
      }
      return [
        item.digits,
        item.result.candidates.map((candidate) => {
          if (
            !matchesProfileLanguage(candidate.label_lang, encodingSystem) ||
            (profile.requiresPronunciationMetadata && (
              candidate.pronunciation_alphabet?.toLowerCase() !== "ipa" ||
              !validPronunciation(candidate.pronunciation) ||
              !validMetadata(candidate.pronunciation_dialect, 1024) ||
              !validMetadata(candidate.pronunciation_variant, 1024)
            )) ||
            (!profile.requiresPronunciationMetadata && (
              candidate.pronunciation !== null &&
              candidate.pronunciation !== undefined ||
              candidate.pronunciation_alphabet !== null &&
              candidate.pronunciation_alphabet !== undefined ||
              candidate.pronunciation_dialect !== null &&
              candidate.pronunciation_dialect !== undefined ||
              candidate.pronunciation_variant !== null &&
              candidate.pronunciation_variant !== undefined
            )) ||
            !validNullableMetadata(candidate.definition, 10_000) ||
            !matchesProfileWikipediaUrl(candidate.wikipedia_url, encodingSystem)
          ) {
            throw new Error(
              `${profile.displayName} shared rows require complete ${profile.displayName} candidate metadata`,
            );
          }
          return [
            candidate.number,
            candidate.noun,
            candidate.pronunciation ?? null,
            candidate.pronunciation_dialect ?? null,
            candidate.pronunciation_variant ?? null,
            candidate.label_lang,
            candidate.definition ?? null,
            candidate.wikipedia_url ?? null,
          ];
        }),
      ];
    });
    validateProfileRows(rows, encodingSystem);
    return rows;
  }
  const rows: CompactSharePayload["r"] = items.map((item) => {
    if (!item.ok) {
      throw new Error("A successful mapping is required");
    }
    return [
      item.digits,
      item.result.candidates.map((candidate) => [candidate.number, candidate.noun]),
    ];
  });
  validateEnglishRows(rows);
  return rows;
}

function expandEnglishRows(value: unknown, stochastic: boolean): BatchEncodeItem[] {
  validateEnglishRows(value);
  return value.map(([digits, pairs]) => {
    const candidates = pairs.map(([number, noun]) => ({
      number,
      noun,
      noun_qid: null,
      label_lang: null,
      root_category: null,
      category_path_qids: [],
      category_path_labels: [],
      pronunciation: null,
      pronunciation_alphabet: null,
      pronunciation_dialect: null,
    }));
    const words = candidates.map(({ noun }) => noun);
    return {
      digits,
      ok: true as const,
      result: {
        digits,
        chunks: candidates.map(({ number }) => number),
        words,
        text: words.join(" "),
        stochastic,
        candidates,
      },
    };
  });
}

function expandProfileRows(
  value: unknown,
  stochastic: boolean,
  encodingSystem: NonEnglishEncodingSystem,
): BatchEncodeItem[] {
  validateProfileRows(value, encodingSystem);
  return value.map(([digits, pairs]) => {
    const candidates = pairs.map(([
      number,
      noun,
      pronunciation,
      pronunciationDialect,
      pronunciationVariant,
      labelLanguage,
      definition,
      wikipediaUrl,
    ]) => ({
      number,
      noun,
      noun_qid: null,
      label_lang: labelLanguage,
      root_category: null,
      category_path_qids: [],
      category_path_labels: [],
      definition,
      pronunciation,
      pronunciation_alphabet: pronunciation === null ? null : "ipa",
      pronunciation_dialect: pronunciationDialect,
      pronunciation_variant: pronunciationVariant,
      wikipedia_url: wikipediaUrl,
    }));
    const words = candidates.map(({ noun }) => noun);
    return {
      digits,
      ok: true as const,
      result: {
        encoding_system: encodingSystem,
        digits,
        chunks: candidates.map(({ number }) => number),
        words,
        text: words.join(" "),
        stochastic,
        candidates,
      },
    };
  });
}

function validateEnglishRows(
  value: unknown,
): asserts value is CompactSharePayload["r"] {
  validateRowEnvelope(value);
  for (const [, pairs] of value) {
    for (const pair of pairs) {
      if (!Array.isArray(pair) || pair.length !== 2) {
        throw new Error(INVALID_SHARE_MESSAGE);
      }
      const [number, noun] = pair as [unknown, unknown];
      if (!validDigits(number) || !validNoun(noun)) {
        throw new Error(INVALID_SHARE_MESSAGE);
      }
    }
  }
}

function validateProfileRows(
  value: unknown,
  encodingSystem: NonEnglishEncodingSystem,
): asserts value is CompactSharePayloadV2["r"] {
  validateRowEnvelope(value);
  for (const [, pairs] of value) {
    for (const pair of pairs) {
      if (!Array.isArray(pair) || pair.length !== 8) {
        throw new Error(INVALID_SHARE_MESSAGE);
      }
      const [
        number,
        noun,
        pronunciation,
        dialect,
        variant,
        labelLanguage,
        definition,
        wikipediaUrl,
      ] = pair as unknown[];
      if (
        !validDigits(number) ||
        !validNoun(noun) ||
        (getEncodingProfile(encodingSystem).requiresPronunciationMetadata && (
          !validPronunciation(pronunciation) ||
          !validMetadata(dialect, 1024) ||
          !validMetadata(variant, 1024)
        )) ||
        (!getEncodingProfile(encodingSystem).requiresPronunciationMetadata && (
          pronunciation !== null || dialect !== null || variant !== null
        )) ||
        typeof labelLanguage !== "string" ||
        !matchesProfileLanguage(labelLanguage, encodingSystem) ||
        !validNullableMetadata(definition, 10_000) ||
        !matchesProfileWikipediaUrl(wikipediaUrl, encodingSystem)
      ) {
        throw new Error(INVALID_SHARE_MESSAGE);
      }
    }
  }
}

function validateRowEnvelope(
  value: unknown,
): asserts value is Array<[string, unknown[]]> {
  if (!Array.isArray(value) || value.length === 0 || value.length > MAX_ROWS) {
    throw new Error(INVALID_SHARE_MESSAGE);
  }
  const seenDigits = new Set<string>();
  for (const row of value) {
    if (!Array.isArray(row) || row.length !== 2) {
      throw new Error(INVALID_SHARE_MESSAGE);
    }
    const [digits, pairs] = row as [unknown, unknown];
    if (!validDigits(digits) || seenDigits.has(digits)) {
      throw new Error(INVALID_SHARE_MESSAGE);
    }
    if (!Array.isArray(pairs) || pairs.length === 0 || pairs.length > MAX_CANDIDATES) {
      throw new Error(INVALID_SHARE_MESSAGE);
    }
    let joinedDigits = "";
    for (const pair of pairs) {
      if (!Array.isArray(pair) || !validDigits(pair[0])) {
        throw new Error(INVALID_SHARE_MESSAGE);
      }
      joinedDigits += pair[0];
    }
    if (joinedDigits !== digits) {
      throw new Error(INVALID_SHARE_MESSAGE);
    }
    seenDigits.add(digits);
  }
}

function validPronunciation(value: unknown): value is string {
  return typeof value === "string" &&
    value.length >= 3 &&
    value.length <= 1024 &&
    ((value.startsWith("/") && value.endsWith("/")) ||
      (value.startsWith("[") && value.endsWith("]")));
}

function validMetadata(value: unknown, maximumLength: number): value is string {
  return typeof value === "string" && value.length >= 1 && value.length <= maximumLength;
}

function validNullableMetadata(value: unknown, maximumLength: number): boolean {
  return value === null || value === undefined || (
    typeof value === "string" && value.length <= maximumLength
  );
}

function validDigits(value: unknown): value is string {
  return typeof value === "string" &&
    value.length >= 1 &&
    value.length <= MAX_DIGITS &&
    /^[0-9]+$/.test(value);
}

function validNoun(value: unknown): value is string {
  return typeof value === "string" &&
    value.length >= 1 &&
    value.length <= MAX_NOUN_LENGTH &&
    value.trim() === value &&
    !Array.from(value).some((character) => {
      const code = character.charCodeAt(0);
      return code <= 31 || code === 127;
    });
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function encodeBase64Url(value: string): string {
  const bytes = new TextEncoder().encode(value);
  let binary = "";
  for (const byte of bytes) {
    binary += String.fromCharCode(byte);
  }
  return btoa(binary)
    .replaceAll("+", "-")
    .replaceAll("/", "_")
    .replace(/=+$/, "");
}

function decodeBase64Url(value: string): string {
  const padded = `${value.replaceAll("-", "+").replaceAll("_", "/")}${"=".repeat((4 - value.length % 4) % 4)}`;
  const binary = atob(padded);
  const bytes = Uint8Array.from(binary, (character) => character.charCodeAt(0));
  return new TextDecoder("utf-8", { fatal: true }).decode(bytes);
}
